import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/styles';
import { SkeletonCardFields } from 'components/Loading';
import {
  Card,
  CardHeader,
  CardContent,
  Grid,
  TextField,
  Tooltip
} from '@material-ui/core';
import 'bootstrap/dist/css/bootstrap.css';
import CloseIcon from '@material-ui/icons/Close';
import { FilterBar } from 'components';
import { getSurveyResults, updateSurveyResultFilters, resetSurveyResultFilters } from 'store/surveys/actions';
import { actions as surveyActions } from 'store/surveys';
import moment from 'moment';
import { Model } from "survey-core";
import { VisualizationPanel } from 'survey-analytics';
import 'survey-analytics/survey.analytics.min.css';

const useStyles = makeStyles(theme => ({
  root: {},
  filterRoot: {
    padding: theme.spacing(2),
    margin: theme.spacing(2),
    backgroundColor: '#f1f1f1'
  },
  cardHeader: {},
  cardActions: {
    flexDirection: 'row'
  },
  inputGrid: {
    maxWidth: '40rem'
  },
  headerContainer: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%'
  },
  title: {
    display: 'flex',
    flexDirection: 'row',
    width: '95%'
  },
  closeIcon: {
    margin: '5px',
    marginTop: '24px'
  },
  surveyResultPanel: {
    '& .sa-toolbar__button:first-child': {
      display: 'none'
    }
  },
  surveyInfoPanel: {
    display: 'flex',
    '& div': {
      width: '100%',
      display: 'flex'
    }
  },
  filterButton: {
    margin: '0px 8px',
    fontWeight: 'normal',
    color: 'rgba(0,0,0,.55) !important',
    opacity: '1 !important'
  },
  selected: {
    color: theme.palette.primary.main,
    margin: '0px 8px'
  }
}));

const Results = props => {
  const { className, staticContext, survey, back, showNav, ...rest } = props;
  const { thisSurvey, thisSurveyResults, isSurveyLoading, closeSurvey } = survey;
  const classes = useStyles();
  const dispatch = useDispatch();
  const { surveyHash } = useParams();
  const associatedCampaigns = useSelector(
    store => store.survey.associatedCampaignList
  );
  const filters = useSelector(
    store => store.survey.surveyResultFilters
  );
  const [campaignStatusFilter, setCampaignStatusFilter] = useState('all');
  const optionsToFilterTags = associatedCampaigns
  ? associatedCampaigns.map(associatedCampaign => {
      return {
        name: 'campaigns',
        displayName: associatedCampaign.name,
        value: associatedCampaign.uuid,
        checked: false,
        active: associatedCampaign.active
      };
    })
  : [];

  useEffect(() => {
    dispatch(resetSurveyResultFilters());
    dispatch(surveyActions.getAssociatedCampaigns({ surveyHash }));
  }, []);

  useEffect(() => {
    if (!isSurveyLoading && thisSurvey.details && thisSurveyResults.results) {
      let questionnaire = thisSurvey?.details && JSON.parse(thisSurvey.details);
      let results = thisSurveyResults?.results || [];

      let survey = new Model(questionnaire);
      let surveyResultNode = document.getElementById('surveyResult');
      surveyResultNode.innerHTML = '';

      let visPanel = new VisualizationPanel(
        survey.getAllQuestions(),
        results,
        { haveCommercialLicense: true }
      );
      visPanel.showHeader = true;
      visPanel.render(surveyResultNode);
    }
  }, [thisSurvey, thisSurveyResults]);

  const getFilteredCampaignOptions = () => {
    if (campaignStatusFilter === 'all') {
      return optionsToFilterTags;
    } else if (campaignStatusFilter === 'active') {
      return optionsToFilterTags.filter(option => option.active === true);
    } else if (campaignStatusFilter === 'archived') {
      return optionsToFilterTags.filter(option => option.active === false);
    }
    return optionsToFilterTags;
  };

  const handleCampaignStatusChange = (newStatus) => {
    setCampaignStatusFilter(newStatus);
  };

  const SkeletonCardContent = props => {
    return (
      <CardContent>
        <Grid container spacing={0}>
          <SkeletonCardFields />
        </Grid>
      </CardContent>
    );
  };

  const filterOptions = {
    autocompleteRadioSearch: {
      options: getFilteredCampaignOptions().map(option => {
        if (filters?.filterTags?.includes(option.value)) {
          option.checked = true;
        }
        return option;
      }),
      title: 'Campaigns',
      values: filters?.filterTags,
      onChange: tags => dispatch(updateSurveyResultFilters({ filterTags: tags, pageNo: 0 })),
      showStatusFilter: true,
      statusFilter: campaignStatusFilter,
      onStatusChange: handleCampaignStatusChange
    },
    dateRangeFilters: {
      startDate: {
        title: 'From Date',
        keyName: 'startDate',
        format: 'YYYY-MM-DD',
        value: filters?.filterStartDate,
        maxDate: moment(),
        action: (startDate) => dispatch(updateSurveyResultFilters({ filterStartDate: startDate, pageNo: 0 })),
      },
      endDate: {
        title: 'To Date',
        keyName: 'endDate',
        format: 'YYYY-MM-DD',
        value: filters?.filterEndDate,
        minDate: filters?.filterStartDate ? filters?.filterStartDate : moment(),
        maxDate: moment(),
        action: (endDate) => dispatch(updateSurveyResultFilters({ filterEndDate: endDate, pageNo: 0 })),
      }
    },
    storeSelector: (store) => store.survey.surveyResultFilters,
    resetFilters: () => dispatch(resetSurveyResultFilters())
  };

  return (
    <div className={classes.root}>
      <Card {...rest} className={clsx(classes.root, className)}>
        <form autoComplete="off">
          <div className={classes.headerContainer}>
            <div className={classes.title}>
              <CardHeader
                title="Survey Results"
                titleTypographyProps={{ variant: 'h1' }}
              />
            </div>
            <div className={classes.closeIcon}>
              <Tooltip title="Close" arrow>
                <CloseIcon onClick={closeSurvey} color="error" />
              </Tooltip>
            </div>
          </div>
          {isSurveyLoading && <SkeletonCardContent />}
          {!isSurveyLoading && (
            <CardContent>
              <Card className={classes.filterRoot}>
                <div className={classes.surveyInfoPanel}>
                  <div >
                    <TextField
                      fullWidth
                      label="Survey Name"
                      margin="dense"
                      name="name"
                      value={thisSurvey.name || ''}
                      required
                      variant="outlined"
                      disabled={true}
                      />
                  </div>
                  <div>
                  </div>
                </div>
                <div className={classes.surveyFilterPanel}>
                {filterOptions && (
                  <FilterBar
                    filterOptions={filterOptions}
                    action={getSurveyResults({surveyHash})}>
                  </FilterBar>
                )}
                </div>
              </Card>
              <div>
                <div id="surveyResult" className={classes.surveyResultPanel}></div>
              </div>
            </CardContent>
          )}
        </form>
      </Card>
    </div>
  );
};

export default Results;
